import { Injectable } from "@angular/core";

export interface FormError {
    key: string;
    contents: string[];
    type: string;
}

@Injectable({
    providedIn: 'root'
})

export class FormValidationHelper {
    private formErrors: FormError[] = [];
    private fieldFailValidators: { [key: string]: string } = {};

    /**
     * Set form errors from API response
     * @param errors Array of form errors from API
     */
    setFormErrors(errors: FormError[]): void {
        this.formErrors = errors || [];
        this.fieldFailValidators = {};
    }

    /**
     * Clear all form errors
     */
    clearFormErrors(): void {
        this.formErrors = [];
        this.fieldFailValidators = {};
    }

    /**
     * Check if a field is invalid based on API errors
     * @param fieldName Name of the field to check
     * @returns true if field has errors, false otherwise
     */
    isFieldInvalid(fieldName: string): boolean {
        /**
         * Expected API error format:
         * [
         *   {
         *     "field": "field_name",
         *     "contents": [
         *       "Message 1",
         *       "Message 2",
         *     ],
         *     "type": "validation_error"
         *   }
         * ]
         */
        const fields = this.formErrors;
        if (fields && fields.length > 0) {
            const field = fields.find(error => error.key.toLowerCase() === fieldName.toLowerCase());
            if (field && field.contents && field.contents.length > 0) {
                // Store field error for later retrieval
                this.fieldFailValidators[fieldName] = field.contents[0];
                return true;
            }
        }
        return false;
    }

    /**
     * Get error message for a specific field
     * @param fieldName Name of the field
     * @returns Error message or empty string if no error
     */
    getFieldError(fieldName: string): string {
        return this.fieldFailValidators[fieldName] || '';
    }

    findError(fieldName: string): string {
        const field = this.formErrors.find(e => e.key.toLowerCase() === fieldName.toLowerCase());
        return field?.contents?.[0] || '';
    }

    /**
     * Get all current form errors
     * @returns Array of form errors
     */
    getFormErrors(): FormError[] {
        return this.formErrors;
    }

    /**
     * Check if form has any errors
     * @returns true if form has errors, false otherwise
     */
    hasErrors(): boolean {
        return this.formErrors.length > 0;
    }

    /**
     * Get error count
     * @returns Number of errors
     */
    getErrorCount(): number {
        return this.formErrors.length;
    }
}
